import React, { useState, useEffect } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import {
    SectionHeader,
    StatCard,
    TablePro,
    Badge,
    Button,
    CardGlass,
    LoadingSpinner
} from "@/components/ui/PremiumComponents";
import {
    Eye, Search, Filter, RefreshCw, UserCheck,
    Calendar, Clock, AlertTriangle, CreditCard,
    ArrowUpRight, ArrowDownRight, MoreHorizontal
} from 'lucide-react';
import { fetchSubscriptions, fetchStats, changeStatus } from "@/services/superadminApi";
import { toast } from "sonner";

export default function SuperAdminSubscriptions() {
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [subscriptions, setSubscriptions] = useState([]);
    const [stats, setStats] = useState(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [limit] = useState(10);

    const loadData = async () => {
        setLoading(true);
        try {
            const [subsData, statsData] = await Promise.all([
                fetchSubscriptions({ page, limit, q: search }),
                fetchStats()
            ]);
            setSubscriptions(subsData.items || []);
            setTotal(subsData.total || 0);
            setStats(statsData);
        } catch (error) {
            console.error("Error cargando suscripciones:", error);
            toast.error("Error al sincronizar con el motor de pagos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [page, search]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleStatusChange = async (companyId, newStatus) => {
        try {
            await changeStatus(companyId, newStatus, "Actualización desde panel administrativo");
            toast.success(`Estado actualizado a ${newStatus}`);
            loadData();
        } catch (error) {
            toast.error("No se pudo cambiar el estado de la suscripción");
        }
    };

    const kpis = [
        {
            label: "Suscripciones Activas",
            value: stats?.subscriptions?.by_status?.active || 0,
            delta: 8,
            trend: 'up',
            icon: <UserCheck />,
            color: '#10B981'
        },
        {
            label: "MRR Estimado",
            value: `€${(stats?.mrr_eur || 0).toLocaleString()}`,
            delta: 12,
            trend: 'up',
            icon: <CreditCard />,
            color: '#8B5CF6'
        },
        {
            label: "En Periodo Trial",
            value: stats?.tenants_trial || 0,
            delta: 2,
            trend: 'down',
            icon: <Clock />,
            color: '#3B82F6'
        },
        {
            label: "Pagos Fallidos",
            value: stats?.subscriptions?.by_status?.past_due || 0,
            delta: 5,
            trend: 'up',
            icon: <AlertTriangle />,
            color: '#EF4444'
        },
    ];

    const renderRow = (row) => (
        <>
            <td className="px-6 py-5">
                <div className="flex flex-col">
                    <span className="font-bold text-white text-lg tracking-tight">{row.company_name}</span>
                    <span className="text-[10px] font-black uppercase tracking-widest mt-1" style={{ color: 'var(--text-muted)' }}>
                        ID: {row.company_id}
                    </span>
                </div>
            </td>
            <td className="px-6 py-5">
                <Badge status={row.plan_id === 'enterprise' ? 'primary' : 'info'}>
                    {row.plan_id?.toUpperCase() || 'GRATUITO'}
                </Badge>
            </td>
            <td className="px-6 py-5">
                <Badge status={
                    row.status === 'active' ? 'success' :
                        row.status === 'trialing' ? 'info' :
                            row.status === 'past_due' ? 'danger' : 'warning'
                }>
                    {row.status?.replace('_', ' ').toUpperCase()}
                </Badge>
            </td>
            <td className="px-6 py-5">
                <div className="flex flex-col">
                    <span className="font-bold text-white text-sm">
                        {row.next_billing_date ? new Date(row.next_billing_date).toLocaleDateString() : 'N/A'}
                    </span>
                    <span className="text-[10px] uppercase font-black tracking-tighter" style={{ color: 'var(--text-soft)' }}>
                        {row.cancel_at_period_end ? 'Finaliza el periodo' : 'Renovación automática'}
                    </span>
                </div>
            </td>
            <td className="px-6 py-5">
                <div className="flex flex-col">
                    <span className="font-black text-white text-base">€{(row.amount || 0).toFixed(2)}</span>
                    <span className="text-[10px] font-medium uppercase tracking-tighter" style={{ color: 'var(--text-muted)' }}>
                        {row.billing_interval === 'year' ? 'Facturación Anual' : 'Facturación Mensual'}
                    </span>
                </div>
            </td>
            <td className="px-6 py-5 text-right">
                <div className="flex justify-end gap-2">
                    <Button variant="secondary" className="px-4 py-2 text-xs flex items-center gap-2">
                        <Eye size={14} /> Gestionar
                    </Button>
                    <button
                        className="p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-400 hover:text-white"
                        onClick={() => handleStatusChange(row.company_id, row.status === 'active' ? 'canceled' : 'active')}
                    >
                        <MoreHorizontal size={18} />
                    </button>
                </div>
            </td>
        </>
    );

    return (
        <SuperAdminLayout>
            <div className="page-fade-in">
                <SectionHeader
                    title="Control de Monetización"
                    subtitle="Gestión global de ingresos, suscripciones y retención de clientes SaaS."
                    actions={
                        <div className="flex items-center gap-4">
                            <div className="relative group overflow-hidden rounded-2xl glass-effect border border-white/5 shadow-inner">
                                <Search
                                    size={18}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 group-focus-within:text-primary transition-colors"
                                    style={{ color: 'var(--text-muted)' }}
                                />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={handleSearch}
                                    placeholder="Buscar por restaurante o ID..."
                                    className="bg-transparent border-none outline-none pl-12 pr-4 py-3 text-white text-sm w-80 font-medium group-focus-within:ring-2 ring-primary/20"
                                />
                            </div>
                            <Button variant="secondary" onClick={loadData} className="p-3">
                                <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                            </Button>
                        </div>
                    }
                />

                {/* KPI Strip */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                    {kpis.map((kpi, i) => (
                        <StatCard key={i} {...kpi} />
                    ))}
                </div>

                <div className="space-y-10">
                    <TablePro
                        headers={["Restaurante / ID", "Plan", "Estado", "Vencimiento", "Importe", "Acciones"]}
                        data={subscriptions}
                        loading={loading}
                        renderRow={renderRow}
                    />

                    {subscriptions.length === 0 && !loading && (
                        <CardGlass className="p-20 text-center">
                            <CreditCard size={48} className="mx-auto mb-4 text-gray-600" />
                            <h3 className="text-xl font-bold text-white mb-2">No se encontraron suscripciones</h3>
                            <p className="text-gray-400 max-w-md mx-auto">
                                No existen registros que coincidan con los criterios de búsqueda actuales.
                                Intenta con otros filtros.
                            </p>
                        </CardGlass>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <CardGlass title="Planes Populares" subtitle="Distribución real de usuarios premium.">
                            <div className="space-y-8 mt-6">
                                {Object.entries(stats?.subscriptions?.by_plan || {}).map(([plan, count]) => {
                                    const percentage = (count / (stats?.subscriptions?.total || 1)) * 100;
                                    return (
                                        <div key={plan} className="flex flex-col gap-2">
                                            <div className="flex justify-between items-center text-[10px] font-black tracking-widest uppercase">
                                                <span style={{ color: 'var(--text-soft)' }}>{plan}</span>
                                                <span className="text-white">{count} ({percentage.toFixed(1)}%)</span>
                                            </div>
                                            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden border border-white/5">
                                                <div
                                                    className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-1000 ease-out"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardGlass>

                        <CardGlass title="Métricas de Retención" subtitle="Análisis de salud del ecosistema.">
                            <div className="grid grid-cols-2 gap-6 mt-6">
                                <div className="p-6 bg-white/5 rounded-3xl border border-white/5 text-center">
                                    <div className="text-2xl font-black text-white mb-1">
                                        €{(stats?.subscriptions?.by_status?.active * 49 || 0).toLocaleString()}
                                    </div>
                                    <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest">MRR Proyectado</div>
                                </div>
                                <div className="p-6 bg-white/5 rounded-3xl border border-white/5 text-center">
                                    <div className="text-2xl font-black text-white mb-1">
                                        {stats?.active_trials || 0}
                                    </div>
                                    <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Leads en Trial</div>
                                </div>
                                <div className="p-6 bg-white/5 rounded-3xl border border-white/5 text-center col-span-2">
                                    <div className="flex justify-center items-center gap-2 text-success font-bold text-lg">
                                        <ArrowUpRight size={20} /> +12%
                                    </div>
                                    <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Crecimiento Mensual</div>
                                </div>
                            </div>
                        </CardGlass>
                    </div>
                </div>
            </div>
        </SuperAdminLayout>
    );
}
